import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Tabs, Dropdown, Flex, Tooltip, Tag } from "antd";
import { MenuOutlined, HistoryOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
import { CustomTable, CustomTableExtend } from "@/hocComponents";
import InvoiceModal from "./component/InvoiceModal";

/* ---------------- 常量 ---------------- */

const PAGE_SIZE = 100;

const TABS = [
  { key: 1, label: "HomeDepot" },
  { key: 2, label: "Foothill" },
  { key: 3, label: "Valle Vista" },
  { key: 4, label: "Max's" },
  { key: 5, label: "Ropain Appliance Inc" },
];

const VENDOR_TYPE_MAP = {
  1: "Maint.Worker",
  2: "Contractor",
};

const INIT_TABLE_HEAD = {
  requestNo: null,
  status: null,
  materialCostStr: null,
  materialRemark: null,
  jobs: null,
  vendorType: null,
  vendorName: null,
  unitAddress: null,
  city: null,
  supervisor: null,
};

const INIT_PAGINATION = { current: 1, pageSize: PAGE_SIZE, total: 0 };
const getRowKey = (record) =>
  record.maintenanceMaterialInvoiceId ?? record.maintenanceId ?? record["#"];

const MainTable = forwardRef((props, ref) => {
  const [tabsKey, setTabsKey] = useState(TABS[0].key);
  const [refreshKey, setRefreshKey] = useState(0);
  const [modal, setModal] = useState({ type: "", record: null });

  const [tableHead, setTableHead] = useState(INIT_TABLE_HEAD);
  const [tableList, setTableList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState(INIT_PAGINATION);
  const tabsKeyRef = useRef(tabsKey);
  tabsKeyRef.current = tabsKey;
  const tableHeadRef = useRef(tableHead);
  tableHeadRef.current = tableHead;
  const controllerRef = useRef(null); // 当前在途请求
  const reqIdRef = useRef(0); 
  /* ---------------- 列表请求 ---------------- */
  const getTableListFn = useCallback(async (current, pageSize, head) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const reqId = ++reqIdRef.current;
    const isStale = () => reqId !== reqIdRef.current;

    const page = current || 1;
    const count = pageSize || PAGE_SIZE;
    const params = {
      page,
      count,
      storeId: tabsKeyRef.current,
      ...(head ?? tableHeadRef.current),
    };

    setTableLoading(true);
    try {
      const response = await http.get(api.maintenanceMaterialShow, {
        params,
        signal: controller.signal,
      });
      if (isStale() || !response) return;
      if (response.code !== "1") {
        commonFn.modalFailedFn(response);
        return;
      }
      const list = (response.data?.list || []).map((item, index) => ({
        ...item,
        "#": (page - 1) * count + index + 1,
      }));
      setTableList(list);
      setPagination({
        current: page,
        pageSize: count,
        total: response.data?.total || 0,
      });
    } catch (err) {
      if (!isStale()) console.error(err);
    } finally {
      if (!isStale()) setTableLoading(false);
    }
  }, []);

  // 卸载时中止请求
  useEffect(
    () => () => {
      reqIdRef.current += 1;
      controllerRef.current?.abort();
    },
    []
  );

  /* ---------------- 列定义 ---------------- */

  const openModal = useCallback(
    (type, record) => setModal({ type, record }),
    []
  );

  const tableFilterFn = useCallback(
    (event, key, type = "Input") => {
      commonFn.tableFilterFn({
        event,
        key,
        type,
        setTableHead,
        getTableListFn,
        pageSize: PAGE_SIZE,
      });
    },
    [getTableListFn]
  );

  const initColumns = useMemo(
    () => [
      {
        title: "Action",
        dataIndex: "viewDetail",
        titleType: "",
        width: 60,
        render: (text, record) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: "Upload Invoice",
                  icon: <HistoryOutlined twoToneColor="#1677FF" />,
                  label: <div>Upload Invoice</div>,
                },
                {
                  key: "Update Invoice",
                  icon: <EditOutlined twoToneColor="#1677FF" />,
                  disabled: !record?.maintenanceMaterialInvoiceId,
                  label: <div>Update Invoice</div>,
                },
              ],
              onClick: (e) => openModal(e.key, record),
            }}
            trigger={["click"]}
          >
            <MenuOutlined className="col-blue" />
          </Dropdown>
        ),
      },
      {
        title: "Request No",
        dataIndex: "requestNo",
        titleType: "Input",
        width: 100,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "requestNo"),
      },
      {
        title: "Status",
        dataIndex: "status",
        titleType: "Input",
        width: 90,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "status"),
        render: (text, record) => (
          <a
            onClick={() => {
              const url = `${window.location.origin}#/JobRequests/Maintenance/Details?id=${record?.maintenanceId}`;
              window.open(url, "_blank", "noopener,noreferrer");
            }}
          >
            <Tag color="#3c8dbc">{text}</Tag>
          </a>
        ),
      },
      {
        title: "Material Cost",
        dataIndex: "materialCost",
        titleType: "Input",
        width: 90,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "materialCostStr"),
        render: (text, record) =>
          record?.materialCost ? (
            <span
              onClick={(e) => {
                e.preventDefault();
                openModal("View Invoice", record);
              }}
              className="custom-border-bottom custom-ellipsis"
              style={{ display: "inline-block" }}
            >
              {commonFn.amountToDollarFn(record.materialCost)}
            </span>
          ) : (
            commonFn.amountToDollarFn(record?.materialCost)
          ),
      },
      {
        title: "Material Remark",
        dataIndex: "materialRemark",
        titleType: "Input",
        isSorter: true,
        sortterMultiple: 3,
        isWrapFlag: 300,
        onChange: (e) => tableFilterFn(e, "materialRemark"),
      },
      {
        title: "Job Type",
        dataIndex: "jobType",
        titleType: "Input",
        width: 200,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "jobs"),
        render: (text) => (
          <Flex gap="4px 0" wrap>
            {text?.map((item) => (
              <Tooltip key={item} title={item}>
                <Tag
                  color="blue"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item}
                </Tag>
              </Tooltip>
            ))}
          </Flex>
        ),
      },
      {
        title: "Vendor Type",
        dataIndex: "vendorType",
        titleType: "Select",
        width: 90,
        isSorter: true,
        sortterMultiple: 3,
        selectOptions: Object.entries(VENDOR_TYPE_MAP).map(([value, label]) => ({
          label,
          value,
        })),
        render: (text) => VENDOR_TYPE_MAP[text] || "",
        onChange: (e) => tableFilterFn(e, "vendorType", "Select"),
      },
      {
        title: "Vendor Name",
        dataIndex: "vendorName",
        titleType: "Input",
        width: 90,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "vendorName"),
        render: (text, record) => (
          <Tooltip title={record.vendorPhone}>{text}</Tooltip>
        ),
      },
      {
        title: "Unit Address",
        dataIndex: "unitAddress",
        titleType: "Input",
        width: 90,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "unitAddress"),
      },
      {
        title: "City",
        dataIndex: "city",
        titleType: "Input",
        width: 90,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "city"),
      },
      {
        title: "Supervisor",
        dataIndex: "supervisor",
        titleType: "Input",
        width: 90,
        isSorter: true,
        sortterMultiple: 3,
        onChange: (e) => tableFilterFn(e, "supervisor"),
      },
      {
        title: "Approved Time",
        dataIndex: "approvedTime",
        width: 90,
        titleType: "",
        isSorter: false,
        render: (text) => text && dayjs(text).format("MM/DD/YYYY"),
      },
    ],
    [openModal, tableFilterFn]
  );

  const [columns, setColumns] = useState(initColumns);

  /* ---------------- 切换 tab / 手动刷新 ---------------- */
  useEffect(() => {
    tableHeadRef.current = INIT_TABLE_HEAD;
    setTableHead(INIT_TABLE_HEAD);
    setColumns(initColumns);
    getTableListFn(1, PAGE_SIZE, INIT_TABLE_HEAD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsKey, refreshKey]);

  const handleTabChange = (key) => {
    setTableList([]);
    setPagination(INIT_PAGINATION);
    setTabsKey(Number(key));
  };

  const handleTableChange = (pager) => {
    getTableListFn(pager.current, pager.pageSize);
  };

  /* ---------------- 弹窗回调 ---------------- */

  const closeModal = useCallback(() => setModal({ type: "", record: null }), []);

  const handleModalSuccess = () => {
    closeModal();
    getTableListFn(pagination.current, pagination.pageSize);
  };

  return (
    <div>
      <CustomTableExtend
        columns={columns}
        setColumns={setColumns}
        initColumns={initColumns}
        refreshKey={refreshKey}
        setRefreshKey={setRefreshKey}
        noShowList={[!commonFn.btnAuthorityFlag("Download") && "download"]}
      />

      <Tabs
        activeKey={String(tabsKey)}
        items={TABS.map((t) => ({ ...t, key: String(t.key) }))}
        onChange={handleTabChange}
      />

  
      <CustomTable
        key={tabsKey}
        refreshKey={refreshKey}
        tableLoading={tableLoading}
        rowKey={getRowKey}
        columns={columns}
        tableList={tableList}
        pagination={pagination}
        handleTableChange={handleTableChange}
        tableScrollY="340px"
      />

      <InvoiceModal
        type={modal.type}
        record={modal.record}
        onClose={closeModal}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
});

export default MainTable;
